// mindmetal-app/web/src/hooks/useDevTools.ts
import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@personic/shared'

// Types
export interface ServiceHealth {
  name: string
  status: 'healthy' | 'degraded' | 'down' | 'unknown'
  uptime: number
  responseTime: number
  lastCheck: string
  errorCount: number
}

export interface SystemMetrics {
  apiCallsToday: number
  errorsToday: number
  avgLatency: number
  activeUsers: number
  peakUsage: string
}

export interface LogEntry {
  id: string
  timestamp: string
  level: 'ERROR' | 'WARN' | 'INFO' | 'DEBUG'
  message: string
  source: string
  metadata?: Record<string, unknown>
}

export interface LogsResponse {
  logs: LogEntry[]
  total: number
  page: number
  limit: number
}

export interface ApiResponse {
  status: number
  statusText: string
  headers: Record<string, string>
  body: unknown
  responseTime: number
}

// Hooks
export function useServiceHealth() {
  return useQuery({
    queryKey: ['admin', 'health', 'services'],
    queryFn: async () => {
      try {
        const { data } = await api.get<{ services: ServiceHealth[] }>('/admin/health/services')
        return data.services
      } catch {
        // Return mock data if endpoint doesn't exist yet
        return getMockServiceHealth()
      }
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

export function useSystemMetrics() {
  return useQuery({
    queryKey: ['admin', 'health', 'metrics'],
    queryFn: async () => {
      try {
        const { data } = await api.get<SystemMetrics>('/admin/health/metrics')
        return data
      } catch {
        // Return mock data if endpoint doesn't exist yet
        return getMockMetrics()
      }
    },
    refetchInterval: 60000, // Refresh every minute
  })
}

export function useLogs(options: {
  level?: string
  search?: string
  startTime?: string
  endTime?: string
  page?: number
  limit?: number
}) {
  const { level, search, startTime, endTime, page = 1, limit = 50 } = options

  return useQuery({
    queryKey: ['admin', 'logs', options],
    queryFn: async () => {
      try {
        const params = new URLSearchParams()
        if (level && level !== 'all') params.set('level', level)
        if (search) params.set('search', search)
        if (startTime) params.set('start', startTime)
        if (endTime) params.set('end', endTime)
        params.set('page', String(page))
        params.set('limit', String(limit))

        const { data } = await api.get<LogsResponse>(`/admin/logs?${params}`)
        return data
      } catch {
        // Return mock data if endpoint doesn't exist yet
        return getMockLogs(level, search, page, limit)
      }
    },
  })
}

export function useApiRequest() {
  return useMutation({
    mutationFn: async ({
      method,
      endpoint,
      headers,
      body,
    }: {
      method: string
      endpoint: string
      headers: Record<string, string>
      body?: string
    }): Promise<ApiResponse> => {
      const startTime = performance.now()

      try {
        const config: Record<string, unknown> = {
          method,
          url: endpoint,
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
        }

        if (body && method !== 'GET') {
          try {
            config.data = JSON.parse(body)
          } catch {
            config.data = body
          }
        }

        const response = await api.request(config)
        const endTime = performance.now()

        return {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers as Record<string, string>,
          body: response.data,
          responseTime: Math.round(endTime - startTime),
        }
      } catch (error: unknown) {
        const endTime = performance.now()
        const axiosError = error as {
          response?: {
            status: number
            statusText: string
            headers: Record<string, string>
            data: unknown
          }
        }

        if (axiosError.response) {
          return {
            status: axiosError.response.status,
            statusText: axiosError.response.statusText,
            headers: axiosError.response.headers,
            body: axiosError.response.data,
            responseTime: Math.round(endTime - startTime),
          }
        }

        throw error
      }
    },
  })
}

// Mock data functions (for development before backend endpoints exist)
function getMockServiceHealth(): ServiceHealth[] {
  return [
    {
      name: 'API Server',
      status: 'healthy',
      uptime: 99.98,
      responseTime: 45,
      lastCheck: new Date().toISOString(),
      errorCount: 0,
    },
    {
      name: 'Database (PostgreSQL)',
      status: 'healthy',
      uptime: 99.99,
      responseTime: 12,
      lastCheck: new Date().toISOString(),
      errorCount: 0,
    },
    {
      name: 'Kong Gateway',
      status: 'healthy',
      uptime: 99.95,
      responseTime: 8,
      lastCheck: new Date().toISOString(),
      errorCount: 2,
    },
    {
      name: 'Redis Cache',
      status: 'healthy',
      uptime: 99.97,
      responseTime: 3,
      lastCheck: new Date().toISOString(),
      errorCount: 0,
    },
    {
      name: 'Anthropic API',
      status: 'healthy',
      uptime: 99.85,
      responseTime: 850,
      lastCheck: new Date().toISOString(),
      errorCount: 5,
    },
    {
      name: 'Background Workers',
      status: 'healthy',
      uptime: 99.90,
      responseTime: 0,
      lastCheck: new Date().toISOString(),
      errorCount: 1,
    },
  ]
}

function getMockMetrics(): SystemMetrics {
  return {
    apiCallsToday: 12847,
    errorsToday: 23,
    avgLatency: 142,
    activeUsers: 47,
    peakUsage: '2:00 PM',
  }
}

function getMockLogs(
  level?: string,
  search?: string,
  page: number = 1,
  limit: number = 50
): LogsResponse {
  const allLogs: LogEntry[] = [
    {
      id: '1',
      timestamp: new Date(Date.now() - 1000).toISOString(),
      level: 'INFO',
      message: 'API request completed: GET /v3/profiles/user-123',
      source: 'api.v3',
      metadata: { duration_ms: 45, status: 200 },
    },
    {
      id: '2',
      timestamp: new Date(Date.now() - 5000).toISOString(),
      level: 'DEBUG',
      message: 'Profile cache hit for canonical_id=user-123',
      source: 'cache.redis',
    },
    {
      id: '3',
      timestamp: new Date(Date.now() - 10000).toISOString(),
      level: 'WARN',
      message: 'Rate limit approaching for tenant acme-corp (85%)',
      source: 'kong.ratelimit',
      metadata: { tenant_id: 'acme-corp', current: 850, limit: 1000 },
    },
    {
      id: '4',
      timestamp: new Date(Date.now() - 30000).toISOString(),
      level: 'ERROR',
      message: 'Failed to connect to Anthropic API: timeout after 30s',
      source: 'distiller.claude',
      metadata: { error_code: 'TIMEOUT', retry_count: 3 },
    },
    {
      id: '5',
      timestamp: new Date(Date.now() - 60000).toISOString(),
      level: 'INFO',
      message: 'Distillation completed for profile prof-456',
      source: 'distiller.v3',
      metadata: { signals_extracted: 12, duration_ms: 2340 },
    },
    {
      id: '6',
      timestamp: new Date(Date.now() - 120000).toISOString(),
      level: 'INFO',
      message: 'New tenant registered: startup-xyz',
      source: 'tenants.registration',
    },
    {
      id: '7',
      timestamp: new Date(Date.now() - 180000).toISOString(),
      level: 'DEBUG',
      message: 'JWT token refreshed for user admin@example.com',
      source: 'auth.jwt',
    },
    {
      id: '8',
      timestamp: new Date(Date.now() - 240000).toISOString(),
      level: 'ERROR',
      message: 'Database connection pool exhausted, waiting for available connection',
      source: 'db.postgres',
      metadata: { pool_size: 20, waiting: 5 },
    },
    {
      id: '9',
      timestamp: new Date(Date.now() - 300000).toISOString(),
      level: 'WARN',
      message: 'Slow query detected: 1.2s for profile aggregation',
      source: 'db.postgres',
      metadata: { query_id: 'agg-001', duration_ms: 1234 },
    },
    {
      id: '10',
      timestamp: new Date(Date.now() - 360000).toISOString(),
      level: 'INFO',
      message: 'Persona generated successfully for profile prof-789',
      source: 'persona.generator',
      metadata: { mode: 'full', confidence: 0.87 },
    },
  ]

  let filteredLogs = allLogs
  if (level && level !== 'all') {
    filteredLogs = filteredLogs.filter((log) => log.level === level)
  }
  if (search) {
    const searchLower = search.toLowerCase()
    filteredLogs = filteredLogs.filter(
      (log) =>
        log.message.toLowerCase().includes(searchLower) ||
        log.source.toLowerCase().includes(searchLower)
    )
  }

  const start = (page - 1) * limit
  const paginatedLogs = filteredLogs.slice(start, start + limit)

  return {
    logs: paginatedLogs,
    total: filteredLogs.length,
    page,
    limit,
  }
}
